import { useEffect, useState } from 'react'
import { useAuth, useUser, RedirectToSignIn } from '@clerk/clerk-react'
import { isSeatLoaded, hasModule, loadCurrentUserSeat } from '../services/tenancy.js'
import AdminDashboard from '../components/admin/AdminDashboard.jsx'

export default function AdminPage() {
  const { isSignedIn, isLoaded, getToken } = useAuth()
  const { user } = useUser()

  // Re-render once the seat cache finishes loading.
  // isSeatLoaded() starts false; once loadCurrentUserSeat resolves it flips.
  const [seatReady, setSeatReady] = useState(isSeatLoaded())

  // If the user navigated directly to /admin (OutlineView never mounted),
  // the seat cache won't have been populated. Load it here.
  useEffect(() => {
    if (isSeatLoaded() || !user?.id) return
    // Expose token getter for admin write calls (assignSeat, etc.)
    window.__clerkGetToken = () => getToken()
    loadCurrentUserSeat(user.id).then(() => setSeatReady(true))
  }, [user?.id, getToken])

  useEffect(() => {
    if (seatReady) return
    // Poll until seat is loaded (typically < 500ms after app init)
    const id = setInterval(() => {
      if (isSeatLoaded()) { setSeatReady(true); clearInterval(id) }
    }, 50)
    return () => clearInterval(id)
  }, [seatReady])

  if (!isLoaded) return null
  if (!isSignedIn) return <RedirectToSignIn />

  if (!seatReady) {
    return (
      <div style={S.loading}>
        <div style={S.loadingText}>Loading…</div>
      </div>
    )
  }

  if (!hasModule('admin')) {
    return (
      <div style={S.denied}>
        <div style={S.deniedBox}>
          <div style={S.deniedTitle}>Access restricted</div>
          <div style={S.deniedMsg}>
            You don't have an admin seat. Contact your administrator.
          </div>
          <a href="/" style={S.backLink}>← Back to projects</a>
        </div>
      </div>
    )
  }

  return <AdminDashboard />
}

/* ── Styles ─────────────────────────────────────────────────────────────── */

const S = {
  loading: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', background: '#f4f7f8',
  },
  loadingText: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 14,
    color: 'rgba(7,39,45,0.45)',
  },
  denied: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', background: '#f4f7f8', padding: 24,
  },
  deniedBox: {
    background: '#fff', borderRadius: 10, border: '0.5px solid rgba(7,39,45,0.12)',
    padding: '32px 40px', maxWidth: 380, textAlign: 'center',
  },
  deniedTitle: {
    fontFamily: '"Montserrat", sans-serif', fontWeight: 700,
    fontSize: 14, letterSpacing: '0.06em', textTransform: 'uppercase',
    color: '#07272D', marginBottom: 10,
  },
  deniedMsg: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 14,
    color: 'rgba(7,39,45,0.65)', lineHeight: 1.55, marginBottom: 20,
  },
  backLink: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 13,
    color: '#1D9E75', textDecoration: 'none',
  },
}
